import React, { useEffect, useRef, useState } from "react";
import { useEditor } from "./EditorContext";
import AIAssistant from "./AIAssistant";

// Topbar moderna do editor (V2). Chama comandos que já existem no GrapesJS
// (undo/redo/canvas-clear/export-template) e os custom registrados no init
// (save-template/save-template-exit/exit/fullscreen-preview/preview-tab).

// ============================================================================
// PILOTO — Assistente de IA (stub 100% frontend, sem chamadas de rede).
// Reverter: trocar para `false` (o botão some) ou comentar o import acima e
// a linha `{USE_AI_ASSISTANT && <AIAssistant />}` no JSX abaixo.
// ============================================================================
const USE_AI_ASSISTANT = true;

const DEVICES = [
  { id: "Desktop", label: "Desktop", icon: "fa fa-desktop" },
  { id: "Tablet", label: "Tablet", icon: "fa fa-tablet-alt" },
  { id: "Mobile portrait", label: "Mobile", icon: "fa fa-mobile-alt" },
];

const EditorTopbar = ({ templateType }) => {
  const { editor } = useEditor();
  const [device, setDevice] = useState("Desktop");
  const [showBorders, setShowBorders] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPos, setPreviewPos] = useState({ top: 0, right: 0 });
  const previewRef = useRef(null);
  const previewBtnRef = useRef(null);

  const importCmd =
    templateType === "email"
      ? "gjs-open-import-template"
      : "gjs-open-import-webpage";

  useEffect(() => {
    if (!editor) return;
    setDevice(editor.getDevice() || "Desktop");
    const swBtn = editor.Panels.getButton("options", "sw-visibility");
    if (swBtn) setShowBorders(!!swBtn.get("active"));
    const onDev = () => setDevice(editor.getDevice() || "Desktop");
    editor.on("change:device", onDev);
    return () => editor.off("change:device", onDev);
  }, [editor]);

  useEffect(() => {
    if (!previewOpen) return;
    const onDocClick = (e) => {
      if (previewRef.current && !previewRef.current.contains(e.target)) {
        setPreviewOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [previewOpen]);

  const run = (cmd) => {
    if (!editor) return;
    try {
      editor.runCommand(cmd);
    } catch {}
  };

  const toggleBorders = () => {
    if (!editor) return;
    if (showBorders) editor.stopCommand("sw-visibility");
    else editor.runCommand("sw-visibility");
    setShowBorders(!showBorders);
  };

  const changeDevice = (id) => {
    if (!editor) return;
    editor.setDevice(id);
    setDevice(id);
  };

  const openPreview = (cmd) => {
    setPreviewOpen(false);
    run(cmd);
  };

  return (
    <div className="v2-topbar">
      {/* Grupo esquerdo — devices → desfazer → demais ferramentas */}
      <div className="v2-topbar-left">
        <div className="v2-topbar-group v2-topbar-devices">
          {DEVICES.map((d) => (
            <button
              key={d.id}
              type="button"
              className={`v2-device-btn ${device === d.id ? "active" : ""}`}
              title={d.label}
              onClick={() => changeDevice(d.id)}
            >
              <i className={d.icon} />
            </button>
          ))}
        </div>

        <div className="v2-topbar-group">
          <button
            type="button"
            className="v2-tb-icon"
            title="Desfazer"
            onClick={() => editor?.UndoManager?.undo?.()}
          >
            <i className="fa fa-undo" />
          </button>
          <span className="v2-tb-sep" />
          <button
            type="button"
            className="v2-tb-icon"
            title="Limpar canvas"
            onClick={() => run("canvas-clear")}
          >
            <i className="fa fa-trash" />
          </button>
          <button
            type="button"
            className="v2-tb-icon"
            title="Importar template"
            onClick={() => run(importCmd)}
          >
            <i className="fa fa-download" />
          </button>
          <button
            type="button"
            className={`v2-tb-icon ${showBorders ? "active" : ""}`}
            title="Exibir bordas dos componentes"
            onClick={toggleBorders}
          >
            <i className="fa fa-border-all" />
          </button>
          <button
            type="button"
            className="v2-tb-icon"
            title="Ver código"
            onClick={() => run("export-template")}
          >
            <i className="fa fa-code" />
          </button>
        </div>
      </div>

      {/* Grupo direito — ações */}
      <div className="v2-topbar-group v2-topbar-actions">
        {USE_AI_ASSISTANT && <AIAssistant />}
        <div className="v2-preview-wrap" ref={previewRef}>
          <button
            ref={previewBtnRef}
            type="button"
            className="v2-btn v2-btn-outline"
            onClick={() => {
              const btn = previewBtnRef.current;
              if (btn) {
                const r = btn.getBoundingClientRect();
                setPreviewPos({
                  top: r.bottom + 6,
                  right: window.innerWidth - r.right,
                });
              }
              setPreviewOpen((v) => !v);
            }}
          >
            <i className="fa fa-eye" /> Visualizar
            <i
              className="fa fa-caret-down"
              style={{ marginLeft: 4, fontSize: 10 }}
            />
          </button>
          {previewOpen && (
            <div
              className="v2-dropdown"
              style={{
                position: "fixed",
                top: previewPos.top,
                right: previewPos.right,
              }}
            >
              <button
                type="button"
                onClick={() => openPreview("fullscreen-preview")}
              >
                <i className="fa fa-expand" /> Tela cheia
              </button>
              <button
                type="button"
                onClick={() => openPreview("preview-tab")}
              >
                <i className="fa fa-external-link-alt" /> Nova aba
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          className="v2-btn v2-btn-primary"
          onClick={() => run("save-template")}
          title="Salvar"
        >
          <i className="fa fa-save" /> Salvar
        </button>
        <button
          type="button"
          className="v2-btn v2-btn-outline"
          onClick={() => run("save-template-exit")}
          title="Salvar e Sair"
        >
          <i className="fa fa-save" /> Salvar e Sair
        </button>
        <button
          type="button"
          className="v2-btn-icon-only v2-btn-ghost"
          onClick={() => run("exit")}
          title="Sair sem salvar"
        >
          <i className="fa fa-times" />
        </button>
      </div>
    </div>
  );
};

export default EditorTopbar;
